import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ImageCarousel.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

const asArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

function normalizeFilename(img) {
  if (!img) return "";
  if (typeof img === "string") return img;
  return img.filename || img.name || "";
}

function getCoverImage(images = []) {
  const arr = asArray(images);
  const coverObj = arr.find((x) => x && typeof x === "object" && x.cover === true);
  return coverObj || arr[0] || null;
}

function sortCoverFirst(images = []) {
  const arr = asArray(images);
  return [...arr].sort((a, b) => {
    const ac = typeof a === "object" && a?.cover ? 0 : 1;
    const bc = typeof b === "object" && b?.cover ? 0 : 1;
    return ac - bc;
  });
}

function slugify(str) {
  return String(str || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// ✅ detect "touch-first" (mobile-like)
function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(hover: none) and (pointer: coarse)")?.matches ||
    "ontouchstart" in window
  );
}

export default function ImageCarousel({
  group,
  groupSlug,
  getImageUrl,
  fontStyles,
  sectionStyles = {},
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // ✅ Mobile: which tile overlay is open (inline, like screenshot)
  const [openTileKey, setOpenTileKey] = useState("");

  const categories = useMemo(() => {
    const items = asArray(group?.items);
    return items
      .map((it) => {
        const label = it?.displayTitle || it?.label || it?.name || "";
        const itemSlug = slugify(it?.label || it?.name || label);
        const images = asArray(it?.images);
        if (!label || !images.length) return null;

        return {
          label,
          itemSlug,
          description: it?.displayDescription || it?.description || "",
          images,
          raw: it,
        };
      })
      .filter(Boolean);
  }, [group]);

  const tabs = useMemo(() => ["ALL", ...categories.map((c) => c.label)], [categories]);
  const [activeTab, setActiveTab] = useState("ALL");

  const gotoCategoryPageTop = useCallback(
    (itemSlugVal) => {
      const path = `/gallery/${groupSlug}/${itemSlugVal}`;
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      navigate(path);
    },
    [groupSlug, navigate]
  );

  // ✅ Close open overlay when changing tab
  useEffect(() => {
    setOpenTileKey("");
  }, [activeTab]);

  // ✅ Close open overlay when tapping outside (mobile)
  useEffect(() => {
    if (!openTileKey) return;

    const onDocClick = () => setOpenTileKey("");
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openTileKey]);

  const viewModel = useMemo(() => {
    // ALL -> only cover per category
    if (activeTab === "ALL") {
      const tiles = categories
        .map((cat) => {
          const coverObj = getCoverImage(cat.images);
          const filename = normalizeFilename(coverObj);
          const imgUrl = getImageUrl(filename);
          if (!imgUrl) return null;

          const coverMetaTitle =
            typeof coverObj === "object" ? coverObj?.metaTitle || "" : "";
          const coverMetaDesc =
            typeof coverObj === "object" ? coverObj?.metaDescription || "" : "";
          const itemMetaTitle = cat.raw?.metaTitle || "";
          const itemMetaDesc = cat.raw?.metaDescription || "";

          return {
            type: "IMAGE",
            key: `all-${cat.itemSlug}`,
            imgUrl,
            title: coverMetaTitle || itemMetaTitle || cat.label || "",
            description: coverMetaDesc || itemMetaDesc || cat.description,
            goToSlug: cat.itemSlug,
          };
        })
        .filter(Boolean);

      return { tiles };
    }

    // Category -> show up to 5 images (+ SEE ALL)
    const cat = categories.find((c) => c.label === activeTab);
    if (!cat) return { tiles: [] };

    const sortedImgs = sortCoverFirst(cat.images);

    const limit = 7;
    const showImgs = sortedImgs.slice(0, limit);

    const tiles = showImgs
      .map((imgObj, idx) => {
        const filename = normalizeFilename(imgObj);
        const imgUrl = getImageUrl(filename);
        if (!imgUrl) return null;

        const metaTitle = typeof imgObj === "object" ? imgObj?.metaTitle || "" : "";
        const metaDesc = typeof imgObj === "object" ? imgObj?.metaDescription || "" : "";
        const itemMetaTitle = cat.raw?.metaTitle || "";
        const itemMetaDesc = cat.raw?.metaDescription || "";

        return {
          type: "IMAGE",
          key: `cat-${cat.itemSlug}-${idx}-${filename}`,
          imgUrl,
          title: metaTitle || itemMetaTitle || cat.label || "",
          description: metaDesc || itemMetaDesc || cat.description,
          goToSlug: cat.itemSlug,
        };
      })
      .filter(Boolean);

    if (sortedImgs.length > limit) {
      tiles.push({
        type: "SEE_ALL",
        key: `seeall-${cat.itemSlug}`,
        title: "SEE ALL",
        subtitle: `${sortedImgs.length} photos`,
        goToSlug: cat.itemSlug,
      });
    }

    return { tiles };
  }, [activeTab, categories, getImageUrl]);

  if (!categories.length) return null;

  const sectionTitle = group?.displayTitle || group?.name || "LATEST PROJECTS";
  const sectionDesc =
    group?.displayDescription ||
    group?.description ||
    "Explore our premium interior work.";

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};

  const sizeOverrides = sectionStyles?.fontSizes || {};

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.heading || sectionTitleFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: sectionStyles?.headingColor || sectionTitleFont?.color || undefined,
  };

  const descStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || sectionDescFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: sectionStyles?.subHeadingColor || sectionDescFont?.color || undefined,
  };

  const tabStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 500,
    lineHeight: fontStyles?.content?.lineHeight || "1.4",
  };

  const tabInactiveColor =
    sectionStyles?.headingColor ||
    sectionTitleFont?.color ||
    fontStyles?.heading?.color ||
    "#111111";

  const tabActiveColor =
    sectionStyles?.contentColor ||
    fontStyles?.content?.color ||
    tabInactiveColor;

  const overlayTitleStyle = {
    fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || fontStyles?.subHeading?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.subHeading?.fontWeight || 600,
    lineHeight: fontStyles?.subHeading?.lineHeight || "1.3",
    color: sectionStyles?.headingColor || fontStyles?.subHeading?.color || undefined,
  };

  const overlayTextStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 400,
    lineHeight: fontStyles?.content?.lineHeight || "1.5",
    color: sectionStyles?.contentColor || fontStyles?.content?.color || undefined,
  };

  return (
    <section className="ic-wrap">
      <div className="ic-inner">
        <header className="ic-header">
          <h2 className="ic-title" style={titleStyle}>{sectionTitle}</h2>
          {sectionDesc ? <p className="ic-desc" style={descStyle}>{sectionDesc}</p> : null}

          <div className="ic-tabs" role="tablist" aria-label="Project Filters">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                className={`ic-tab ${activeTab === t ? "active" : ""}`}
                onClick={() => setActiveTab(t)}
                style={{
                  ...tabStyle,
                  color: activeTab === t ? tabActiveColor : tabInactiveColor,
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </header>

        <div className="ic-grid">
          {viewModel.tiles.map((tile, idx) => {
            const isFeatured = idx === 0 && tile.type === "IMAGE";
            const isOpen = openTileKey === tile.key;

            if (tile.type === "SEE_ALL") {
              return (
                <div
                  key={tile.key}
                  className="ic-tile ic-seeall"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    gotoCategoryPageTop(tile.goToSlug);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      gotoCategoryPageTop(tile.goToSlug);
                    }
                  }}
                >
                  <div className="ic-seeall-box">
                    <div className="ic-seeall-title" style={titleStyle}>{tile.title}</div>
                    <div className="ic-seeall-sub" style={descStyle}>{tile.subtitle}</div>
                    <div className="ic-seeall-btn" style={tabStyle}>View Gallery →</div>
                  </div>
                </div>
              );
            }

            const handleTileClick = (e) => {
              e.stopPropagation();

              // ✅ Mobile: tap toggles inline overlay, DOES NOT navigate
              if (isTouchDevice()) {
                setOpenTileKey((prev) => (prev === tile.key ? "" : tile.key));
                return;
              }

              // ✅ Desktop: click navigates
              gotoCategoryPageTop(tile.goToSlug);
            };

            return (
              <div
                key={tile.key}
                className={`ic-tile ${isFeatured ? "featured" : ""} ${isOpen ? "tap-open" : ""}`}
                onClick={handleTileClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTileClick(e);
                  }
                }}
              >
                <img className="ic-img" src={tile.imgUrl} alt={tile.title || "image"} />

                {/* Desktop hover OR Featured show OR Mobile tap-open */}
                <div className={`ic-overlay ${isFeatured ? "show" : ""} ${isOpen ? "show tap" : ""}`}>
                  {tile.title ? <div className="ic-ov-title" style={overlayTitleStyle}>{tile.title}</div> : null}
                  {tile.description ? <div className="ic-ov-text" style={overlayTextStyle}>{tile.description}</div> : null}

                  {/* ✅ Only visible on mobile tap-open */}
                  <button
                    type="button"
                    className="ic-ov-seemore"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      ev.preventDefault();
                      gotoCategoryPageTop(tile.goToSlug);
                    }}
                  >
                    See more →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
